<template>
  <div class="login-page">
    <div class="login-container">
      <div class="login-card">
        <div class="login-header">
          <div class="logo">
          <img src="/images/logo.png" alt="DMS Logo" class="logo-image">
        </div>
          <p>Automatisation intelligente des emails</p>
        </div>
        
        <form @submit.prevent="handleLogin" class="login-form">
          <div class="form-group">
            <label for="email">
              <i class="fas fa-envelope"></i>
              Email
            </label>
            <input
              id="email"
              v-model="form.email"
              type="email"
              class="form-control"
              :class="{ 'is-invalid': errors.email }"
              placeholder="votre@email.com"
              required
            >
            <div v-if="errors.email" class="invalid-feedback">
              {{ errors.email }}
            </div>
          </div>
          
          <div class="form-group">
            <label for="password">
              <i class="fas fa-lock"></i>
              Mot de passe
            </label>
            <div class="password-input">
              <input
                id="password"
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                class="form-control"
                :class="{ 'is-invalid': errors.password }"
                placeholder="••••••••"
                required
              >
              <button
                type="button"
                class="password-toggle"
                @click="showPassword = !showPassword"
              >
                <i :class="showPassword ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
              </button>
            </div>
            <div v-if="errors.password" class="invalid-feedback">
              {{ errors.password }}
            </div>
          </div>
          
          <div class="form-group">
            <label class="checkbox-label">
              <input v-model="form.remember" type="checkbox">
              <span class="checkmark"></span>
              Se souvenir de moi
            </label>
          </div>
          
          <div v-if="errors.general" class="alert alert-danger">
            <i class="fas fa-exclamation-circle"></i>
            {{ errors.general }}
          </div>
          
          <button
            type="submit"
            class="btn-login"
            :disabled="loading"
          >
            <span v-if="loading" class="spinner"></span>
            <i v-else class="fas fa-sign-in-alt"></i>
            {{ loading ? 'Connexion...' : 'Se connecter' }}
          </button>
        </form>
        
        <div class="login-footer">
          <a href="#" class="forgot-password">
            <i class="fas fa-question-circle"></i>
            Mot de passe oublié ?
          </a>
        </div>
      </div>
      
      <div class="login-info">
        <div class="info-card">
          <h3>
            <i class="fas fa-robot"></i>
            IA Intégrée
          </h3>
          <p>Extraction automatique des commandes avec Ollama et OpenAI</p>
        </div>
        
        <div class="info-card">
          <h3>
            <i class="fas fa-shield-alt"></i>
            Sécurisé
          </h3>
          <p>Chiffrement des données et authentification OAuth2</p>
        </div>
        
        <div class="info-card">
          <h3>
            <i class="fas fa-chart-line"></i>
            Analytics
          </h3>
          <p>Tableaux de bord et détection d'anomalies en temps réel</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'

export default {
  name: 'Login',
  setup() {
    const router = useRouter()
    const authStore = useAuthStore()
    
    const loading = ref(false)
    const showPassword = ref(false)
    
    const form = reactive({
      email: '',
      password: '',
      remember: false
    })
    
    const errors = reactive({
      email: '',
      password: '',
      general: ''
    })
    
    const validateForm = () => {
      let isValid = true
      
      // Reset errors
      Object.keys(errors).forEach(key => {
        errors[key] = ''
      })
      
      // Validate email
      if (!form.email) {
        errors.email = 'L\'email est requis'
        isValid = false
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
        errors.email = 'Format d\'email invalide'
        isValid = false
      }
      
      // Validate password
      if (!form.password) {
        errors.password = 'Le mot de passe est requis'
        isValid = false
      } else if (form.password.length < 6) {
        errors.password = 'Le mot de passe doit contenir au moins 6 caractères'
        isValid = false
      }
      
      return isValid
    }
    
    const handleLogin = async () => {
      if (!validateForm()) {
        return
      }
      
      loading.value = true
      errors.general = ''
      
      try {
        const result = await authStore.login({
          email: form.email,
          password: form.password,
          remember: form.remember
        })
        
        if (result.success) {
          router.push('/')
        } else {
          errors.general = result.message || 'Erreur de connexion'
        }
      } catch (error) {
        console.error('Erreur de connexion:', error)
        errors.general = 'Une erreur est survenue lors de la connexion'
      } finally {
        loading.value = false
      }
    }
    
    return {
      form,
      errors,
      loading,
      showPassword,
      handleLogin
    }
  }
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.login-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  max-width: 1200px;
  width: 100%;
  align-items: center;
}

.login-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.login-header {
  text-align: center;
  margin-bottom: 2rem;
}

.logo {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  padding: 10px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.logo-image {
  width: 120px;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  margin-bottom: 10px;
}

.login-header h1 {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.login-header p {
  color: #718096;
  font-size: 1.1rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 0.5rem;
}

.form-group label i {
  margin-right: 0.5rem;
  color: #667eea;
  width: 16px;
}

.form-control {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-control.is-invalid {
  border-color: #f56565;
}

.password-input {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #718096;
  cursor: pointer;
  padding: 0.25rem;
}

.password-toggle:hover {
  color: #667eea;
}

.checkbox-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.checkbox-label input {
  margin-right: 0.5rem;
}

.invalid-feedback {
  color: #f56565;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.alert {
  padding: 0.75rem 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
}

.alert-danger {
  background: rgba(245, 101, 101, 0.1);
  color: #f56565;
  border: 1px solid rgba(245, 101, 101, 0.2);
}

.alert i {
  margin-right: 0.5rem;
}

.btn-login {
  width: 100%;
  padding: 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-login:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
}

.btn-login:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-login i {
  margin-right: 0.5rem;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 0.5rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.login-footer {
  text-align: center;
  margin-top: 2rem;
}

.forgot-password {
  color: #667eea;
  text-decoration: none;
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
}

.forgot-password:hover {
  text-decoration: underline;
}

.forgot-password i {
  margin-right: 0.25rem;
}

.login-info {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.info-card {
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
}

.info-card h3 {
  display: flex;
  align-items: center;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.info-card h3 i {
  margin-right: 0.75rem;
  font-size: 1.5rem;
}

.info-card p {
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.6;
  margin: 0;
}

/* Tablette & téléphone : une seule colonne, formulaire en premier,
   atouts condensés en dessous */
@media (max-width: 991.98px) {
  .login-page {
    align-items: flex-start;
    padding: 2rem 1.25rem;
  }

  .login-container {
    grid-template-columns: minmax(0, 1fr);
    gap: 1.5rem;
    max-width: 520px;
  }

  .login-card {
    padding: 2rem;
  }

  .login-info {
    gap: 0.75rem;
  }

  .info-card {
    padding: 1rem 1.25rem;
    border-radius: 12px;
  }

  .info-card h3 {
    font-size: 1.05rem;
    margin-bottom: 0.25rem;
  }

  .info-card h3 i {
    font-size: 1.1rem;
    width: 1.25rem;
    text-align: center;
  }

  .info-card p {
    font-size: 0.9rem;
    line-height: 1.45;
    padding-left: 2rem;
  }
}

@media (max-width: 575.98px) {
  .login-page {
    padding: 1.5rem 1rem;
  }

  .login-card {
    padding: 1.75rem 1.25rem;
    border-radius: 16px;
  }

  .login-header {
    margin-bottom: 1.5rem;
  }

  .login-header p {
    font-size: 1rem;
  }

  .form-group {
    margin-bottom: 1.25rem;
  }

  .form-control {
    min-height: 48px;
  }

  .password-input .form-control {
    padding-right: 3rem;
  }

  .login-footer {
    margin-top: 1.5rem;
  }

  .forgot-password {
    min-height: 44px;
  }
}

@media (pointer: coarse) {
  .password-toggle {
    right: 0.25rem;
    min-width: 44px;
    min-height: 44px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
}
</style>